import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import styles from '../TransactionSimpleDetail.module.css';
import MultipleSelect, { MultipleSelectItem } from '../../../selectors/MultipleSelect';
import clsx from 'clsx';
import { BrokerConfigDto, QueueConfigDto, TopicConfigDto } from '../../../../types/rest/mockServiceTypes';
import _ from 'lodash';
import {
  MessagingTransactionMapping,
  MessagingMappings,
  MessagingSourceType,
  MsgTransaction,
} from '../../../../types/rest/assetCatalogTypes';
import { useIsMount } from '../../../../hooks/useIsMountHook';
import { getMappingItems } from '../../../../utils/messagingUtils';

interface Props {
  onUpdateBindings: (mappings: MessagingMappings) => void;
  transaction: MsgTransaction;
  brokerConfig: BrokerConfigDto;
  disabled?: boolean;
}

interface InboundMapping {
  sourceName: string;
  sourceType: MessagingSourceType;
}

/**
 * Queue / Topic binding for Messaging transactions.
 * @param props
 * @constructor
 */
const MessagingBindings = (props: Props) => {
  const getInitialInbound = (): InboundMapping => {
    const { messagingTransactionMappings } = props.transaction;
    if (!messagingTransactionMappings) {
      return null;
    }

    return {
      sourceName: messagingTransactionMappings.sourceName,
      sourceType: messagingTransactionMappings.sourceType,
    };
  };

  const getInitialOutboundList = (): MultipleSelectItem[] => {
    const { messagingTransactionMappings } = props.transaction;
    if (!messagingTransactionMappings) {
      return [];
    }

    return _.map(messagingTransactionMappings.destinations, item => ({
      label: item.destinationName,
      value: item.destinationName,
      metadata: item.destinationType,
    }));
  };

  const [inbound, setInbound] = useState<InboundMapping | null>(getInitialInbound());
  const [outboundList, setOutboundList] = useState<MultipleSelectItem[]>(getInitialOutboundList());
  const isMount = useIsMount();

  useEffect(() => {
    if (!isMount) {
      if (isInboundChanged()) {
        setInbound(getInitialInbound());
      }
      if (isOutboundChanged()) {
        setOutboundList(getInitialOutboundList);
      }
    }
  }, [props.transaction.messagingTransactionMappings]);

  const isInboundChanged = () => {
    return props.transaction.messagingTransactionMappings.sourceName !== inbound.sourceName;
  };

  const isOutboundChanged = () => {
    const destinationCount = _.size(props.transaction.messagingTransactionMappings.destinations);
    return destinationCount !== _.size(outboundList);
  };

  /**
   * If inbound or outbound settings change, patch the transaction on a backend.
   */
  useEffect(() => {
    if (!isMount) {
      const outboundDestinations: MessagingTransactionMapping[] = _.map(outboundList, item => ({
        destinationName: item.value,
        destinationType: _.get(item, 'metadata.type'),
      }));
      //Send update only if there is some change
      if (isInboundChanged() || isOutboundChanged()) {
        props.onUpdateBindings({
          destinations: outboundDestinations,
          sourceName: inbound ? inbound.sourceName : '',
          sourceType: inbound ? inbound.sourceType : 'QUEUE',
        });
      }
    }
  }, [inbound, outboundList]);

  const getSelectedInbound = (): MultipleSelectItem => {
    if (inbound) {
      return { label: inbound.sourceName, value: inbound.sourceName };
    }
    return null;
  };

  return (
    <>
      <Row>
        <Col className={clsx(styles.titleMain, 'mt-2')}>
          <Form.Label>Inbound:</Form.Label>
        </Col>
        <Col className={styles.value}>
          <MultipleSelect
            onChange={(item: any) => {
              setInbound({ sourceName: item.value, sourceType: item.metadata.type });
            }}
            items={getMappingItems(props.brokerConfig)}
            selected={getSelectedInbound()}
            readonly={props.disabled}
            placeholder={'Choose inbound queue / topic'}
            isMulti={false}
          />
        </Col>
      </Row>
      <Row>
        <Col className={clsx(styles.titleMain, 'mt-2')}>
          <Form.Label>Outbound:</Form.Label>
        </Col>
        <Col className={styles.value}>
          <MultipleSelect
            onChange={(items: any) => {
              setOutboundList(items);
            }}
            items={getMappingItems(props.brokerConfig)}
            selected={outboundList}
            readonly={props.disabled}
            placeholder={'Choose outbound queue / topic'}
            isMulti={true}
          />
        </Col>
      </Row>
    </>
  );
};

export default MessagingBindings;
